/* eslint-disable radix */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-console */

import Cookies from 'js-cookie';
import { getAmsCookie } from './ad-ams-cookie-utils';
import { AdExp, AdExpType } from './ad-exps';
import { getGameSession } from './ad-game-session-utils';
import { AdInfo } from './types/ad';
import { AdReportItem } from './types/ad-report';
import { AdRequest } from './types/ad-request';
import { NetworkConnectionType } from './types/ad-request-enum';

export class AdUtils {
  public static isDebugEnv = () => {
    let env = '0';
    if (import.meta.env.EVO && vn.external && vn.external.getUserString) {
      // 0正式环境， 1 UN测试环境，2 UN体验环境，
      env = vn.external.getUserString('UN', 'UNEnv', '0');
    }
    return env === '1';
  };

  public static getAdPrintInfo(adInfo: AdInfo | null, extra = '') {
    if (!adInfo) {
      return `[orderId=null,extra=${extra}]`;
    }
    const orderId = adInfo?.orderId ?? '';
    let title = adInfo?.title ?? '';
    if (!title) {
      title = adInfo?.subTitle ?? '';
    }
    if (extra) {
      return `[orderId=${orderId},title=${title},extra=${extra}]`;
    }
    return `[orderId=${orderId},title=${title}]`;
  }

  public static objectToUrlParams = (data: Record<string, any>) => {
    const strArr = [];
    for (const key in data) {
      if (!key) continue;
      strArr.push(`${key}=${encodeURIComponent(data[key])}`);
    }
    return strArr.join('&');
  };

  public static getTodayKeyWithNoZero = () => {
    const date = new Date();
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  public static getTodayKey = () => this.getDateKey(new Date());

  public static getDateKey = (date: Date) => {
    // "yyyy-MM-dd"
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString()
      .padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  public static getWeekNumber = (date: Date) => {
    const now = date;
    const year = now.getFullYear();
    const weekDay = now.getDay();

    // 计算当年第一天的日期
    const firstDayOfYear = new Date(year, 0, 1);
    // 计算当前日期与当年第一天的日期差
    const dayDifference = (now.getTime() - firstDayOfYear.getTime()) / (1000 * 60 * 60 * 24);
    // 计算周数，注意一周是从周日开始的，所以要考虑这一点
    let weekNumber = Math.floor(dayDifference / 7) + 1;

    // 如果当年第一天是周一到周六之间，那么第一周只算作上周的一部分，需要调整周数
    if (weekDay < firstDayOfYear.getDay()) {
      weekNumber -= 1;
    }

    return weekNumber;
  };

  public static getNetStatusReportValue = () => {
    if (import.meta.env.EVO && vn && vn.external && vn.external.isOnline) {
      if (vn.external.isOnline()) {
        return `${NetworkConnectionType.ETHERNET}`;
      }
      return `${NetworkConnectionType.NO_NETWORK}`;
    }
    return `${NetworkConnectionType.UNKNOWN}`;
  };

  public static deepClone<T>(target: T): T {
    if (target === null) {
      return target;
    }
    if (typeof target !== 'object') {
      return target;
    }
    if (Array.isArray(target)) {
      return target.map(this.deepClone) as unknown as T;
    }
    const result: { [P in keyof T]: T[P] } = { ...target };
    for (const key in result) {
      result[key] = this.deepClone(result[key]);
    }
    return result;
  }

  public static extractUrlParams = (url: string) => {
    if (!url) {
      return {};
    }
    const urlStr = decodeURIComponent(url);
    // 提取 URL 中的查询字符串部分
    const queryString = urlStr.split('?')[1];
    if (!queryString) {
      return {};
    }

    // 分割查询字符串为键值对数组
    const pairs = queryString.split('&');

    // 将键值对数组转换为字典（对象）
    const params: Record<string, string> = {};
    pairs.forEach((pair) => {
      const [key, value] = pair.split('=');
      params[key] = decodeURIComponent(value || ''); // 如果没有值，则默认为空字符串
    });

    return params;
  };

  public static requestInfo2reportItem = (
    reports: AdRequest.AdReportInfo[],
  ): AdReportItem[] => {
    if (!reports) {
      return [];
    }
    const items: AdReportItem[] = [];
    for (const report of reports) {
      items.push({
        url: report.url || '',
        delay: report.report_delay || 0,
      });
    }
    return items;
  };

  public static record2reportItem = (
    reports: Record<string, string>[],
  ): AdReportItem[] => {
    if (!reports) {
      return [];
    }
    const items: AdReportItem[] = [];
    for (const report of reports) {
      items.push({
        url: report.url || '',
        delay: parseInt(report.report_delay) || 0,
      });
    }
    return items;
  };

  public static getVuid(): string | undefined {
    if (!import.meta.env.EVO) {
      return '';
    }
    const cookie = vn?.external?.getLoginCookie() || '';
    const mainLogin = cookie.match(/\bmain_login=([^;]+)/)?.[1];
    if (mainLogin === 'qq') {
      return cookie.match(/\bvqq_vuserid=([^;]+)/)?.[1];
    }
    if (mainLogin === 'wx') {
      return cookie.match(/\bvuserid=([^;]+)/)?.[1];
    }
  }

  public static getGUID(): string | undefined {
    if (!import.meta.env.EVO) {
      return '';
    }
    return vn?.external?.getUNRequestManagerInitializeParam().guid || '';
  }
}

export function getQrCodeLocalPath(qrCodeUrl: string) {
  return qrCodeUrl && qrCodeUrl.length !== 0 ? `qrcode://url=${encodeURIComponent(qrCodeUrl)}` : '';
}

export function safeParseJson<T>(json: string): T | Record<any, any> {
  if (json === '') {
    return null;
  }
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export function safeParseArr(json: string) {
  if (json === '') {
    return [];
  }
  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
}

export function getAdUserAgent() {
  if (!import.meta.env.EVO) {
    return '';
  }
  return vn?.external?.getAdUserAgent?.() ?? 'Mozilla/5.0 (Windows NT 6.2; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) QQLive/11788522/50231906 Chrome/43.0.2357.134 Safari/537.36 QBCore/3.43.21.400 QQBrowser/9.0.2524.400';
}

export function setStorage(key: string, data: any) {
  return new Promise<void>((resolve) => {
    if (import.meta.env.EVO) {
      vn?.storage?.setStorage({
        key,
        data,
        success: resolve,
        fail: () => {
          resolve(void 0);
        },
        complete: () => {
          resolve(void 0);
        },
        expires: Number.MAX_SAFE_INTEGER,
      });
    } else {
      localStorage.setItem(key, JSON.stringify(data));
      resolve(void 0);
    }
  });
}

const isMac = () => (import.meta.env.EVO ? vnContext.external.osType() === 'mac' : false);

export function getStorage(key: string) {
  return new Promise<any>((resolve) => {
    if (import.meta.env.EVO) {
      vn?.storage?.getStorage({
        key,
        success: resolve,
        fail: () => {
          resolve(void 0);
        },
        complete: () => {
          resolve(void 0);
        },
      });
    } else {
      const str = localStorage.getItem(key);
      resolve(str);
    }
  });
}

export const getPcSdkVersion = () => {
  let version = '';
  if (import.meta.env.EVO && !isMac()) {
    version = vn?.external?.getAmsSdkVersion?.() ?? '0.0.6';
  }
  return version;
};

export const getOaid = () => {
  let oaid = '';
  if (import.meta.env.EVO && !isMac()) {
    oaid = vn?.external?.getOaid?.() ?? '';
  }
  return oaid;
};

// 应用宝环境
export const getYYBSDKEnv = () => {
  let support_pc_yyb_mobile_app_engine = '';
  if (import.meta.env.EVO && !isMac()) {
    support_pc_yyb_mobile_app_engine = (vn?.external?.checkYYBSDKEnv?.() ?? false) ? '1' : '0';
  }
  return support_pc_yyb_mobile_app_engine;
};

// 广告UnionId
export const getAdUnionId = () => {
  let pc_device_info = '';
  if (import.meta.env.EVO && !isMac()) {
    pc_device_info = (vn?.external?.checkYYBSDKEnv?.() ?? false) ? '1' : '0';
  }
  return pc_device_info;
};

// 广告请求额外透传字段
export function getAdExtraRequestParams() {
  let pc_sdk_version = '';
  let pc_device_info = '';
  let pc_oaid = '';
  let support_pc_yyb_mobile_app_engine = '0';
  let pc_wegame_version = '';
  let ad_wechat_authorization_status = '0';
  if (import.meta.env.EVO && !isMac()) {
    if (vn?.external?.getAmsSdkVersion) {
      pc_sdk_version = vn.external.getAmsSdkVersion() || '0.0.6';
    }
    if (vn?.external?.getAdUnionId) {
      pc_device_info = vn.external.getAdUnionId() || '';
    }
    if (vn?.external?.getOaid) {
      pc_oaid = vn.external.getOaid() || '';
    }
    if (vn?.external?.checkYYBSDKEnv) {
      support_pc_yyb_mobile_app_engine = vn.external.checkYYBSDKEnv() ? '1' : '0';
    }
    if (vn?.external?.getWegameSDKVersion) {
      pc_wegame_version = vn.external.getWegameSDKVersion() || '';
    }
    if (vn?.external?.isAdWxGameAuth) {
      ad_wechat_authorization_status = vn.external.isAdWxGameAuth() ? '1' : '2';
    }
  }
  return {
    pc_wegame_version,
    pc_sdk_version,
    pc_device_info,
    pc_oaid,
    support_pc_yyb_mobile_app_engine,
    ad_wechat_authorization_status,
  };
}

export const getThoughoutAdParams = () => (import.meta.env.EVO
  ? vn.external.getUNRequestManagerInitializeParam().thoughoutStrategyAdParams || ''
  : '');

export async function getChannelAdRequestParams() {
  const common = {
    // 广告参数透传
    ad_exp_ids: AdExp.getVlAdExpIds(AdExpType.AD_EXP_TYPE_CHANNEL),
    ams_cookies: await getAmsCookie(),
    ad_trans_data: JSON.stringify({
      game_sessions: getGameSession(),
    }),
  };

  // 在 web 上
  if (!import.meta.env.EVO) {
    return {
      ...common,
      skip_privacy_types: import.meta.env.SSR ? '0' : Cookies.get('skip_privacy_types') || '0',
      support_click_scan: '1',
    };
  }

  // 在 windows 上
  if (import.meta.env.EVO && !isMac()) {
    return {
      ...common,
      'ad-through-tag': getThoughoutAdParams(),
      ...getAdExtraRequestParams(),
    };
  }

  return {};
}

export function getDetailAdRequestParams() {
  return {
    ad_exp_ids: AdExp.getVlAdExpIds(AdExpType.AD_EXP_TYPE_VIDEO_DETAIL),
    ad_trans_data: JSON.stringify({
      game_sessions: getGameSession(),
    }),
    ...getAdExtraRequestParams(),
  };
}
